import React, { FunctionComponent } from 'react';
import { IconRoundCheck, IconCalendar } from '@belong/icons';
import { BillInfoModel, TransferInfoModel } from 'api/models';
import classnames from 'classnames/bind';
import styles from './bill-status-icon.module.css';

type BillStatusIconProps = {
  status: BillInfoModel['status'] | TransferInfoModel['status'];
};

const cx = classnames.bind(styles);

// White warning triangle with a red exclamation, rendered on the red status
// circle. The shared IconAlert is a red triangle with a white mark, which reads
// as solid red on the red circle.
function IconExclamation(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.7672 12.7742L9.16893 0.712996C8.92762 0.27214 8.48121 0 7.9986 0C7.51599 0 7.06957 0.27214 6.82827 0.712996L0.180979 12.861C-0.0603263 13.3019 -0.0603263 13.8461 0.180979 14.287C0.422284 14.7279 0.868699 15 1.35131 15H14.6449H14.6487C15.3952 15 16 14.3618 16 13.574C15.9992 13.278 15.914 13.0027 15.767 12.7743L15.7672 12.7742Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.00002 4.19458C8.41703 4.19458 8.75486 4.55108 8.75486 4.99114L8.56182 9.74417C8.54221 10.1739 8.22625 10.337 8.00002 10.337C7.74665 10.337 7.46009 10.1962 7.43822 9.74417L7.24518 4.99114C7.24518 4.55108 7.58301 4.19458 8.00002 4.19458Z"
        fill="#FF5661"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.68169 12.7486C8.50373 12.938 8.25489 13.0462 8.00001 13.0462C7.74514 13.0462 7.49628 12.938 7.31681 12.7486C7.13734 12.5584 7.03479 12.2958 7.03479 12.0269C7.03479 11.7579 7.13734 11.4969 7.31681 11.3075C7.49628 11.1181 7.74514 11.0083 8.00001 11.0083C8.25489 11.0083 8.50374 11.1181 8.68169 11.3075C8.86267 11.4977 8.96523 11.7603 8.96523 12.0269C8.96523 12.2958 8.86268 12.5584 8.68169 12.7486Z"
        fill="#FF5661"
      />
    </svg>
  );
}

export function BillStatusIcon({ status }: BillStatusIconProps) {
  const Icon = ICONS[status] || IconCalendar;
  const bgColor = BG_COLORS[status] || 'bg-blue';

  return (
    <div
      className={cx(
        'wrapper',
        'flex items-center justify-center rounded-full',
        bgColor,
      )}
    >
      <Icon height={15} className="text-white" />
    </div>
  );
}

const BG_COLORS: Record<
  BillInfoModel['status'] | TransferInfoModel['status'],
  string
> = {
  Failed: 'bg-red',
  Paid: 'bg-green',
  PartiallyRefunded: 'bg-blue',
  Published: 'bg-blue',
  Refunded: 'bg-gray',
  Unpublished: 'bg-blue',
  Void: 'bg-blue',
  Initiated: 'bg-blue',
  Canceled: 'bg-red',
  Completed: 'bg-green',
};

const ICONS: Record<
  BillInfoModel['status'] | TransferInfoModel['status'],
  FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  Failed: IconExclamation,
  Paid: IconRoundCheck,
  PartiallyRefunded: IconCalendar,
  Published: IconCalendar,
  Refunded: IconCalendar,
  Unpublished: IconCalendar,
  Void: IconCalendar,
  Initiated: IconCalendar,
  Canceled: IconCalendar,
  Completed: IconCalendar,
};
